import { CUSTOMERS, api, validateTemplateContent } from '@agency/shared';
import { Button, Callout, Card, Chip, Code, Dialog, EmptyState, Field, Input, KeyValue, PageHeader, Select, Skeleton, Tabs, Textarea, fmtTimestamp } from '@agency/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCallout, Grid, IdCell, JsonEditor, Page, SensitiveButton, Stamp, UserLink } from '../../app/widgets.js';
import { ApiError } from '../../lib/api.js';
import { ROLLOUT_LABEL, TEMPLATE_STATE_LABEL, TEMPLATE_STATE_TONE, TEMPLATE_TYPE_SINGULAR } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';

type Panel = 'editor' | 'validation' | 'preview' | 'diff';

const TEXT_TYPES: readonly api.TemplateType[] = ['agenda_template', 'email_template', 'remediation_ask', 'help_content'];

function renderMarkdown(md: string): string {
  const html = marked.parse(md, { async: false, gfm: true, breaks: false });
  return DOMPurify.sanitize(html, { FORBID_TAGS: ['img', 'style', 'script', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math'], FORBID_ATTR: ['style', 'onerror', 'onload'], ALLOWED_URI_REGEXP: /^https:\/\//i });
}

export function TemplateDetailPage() {
  const { templateId } = useParams({ strict: false }) as { templateId: string };
  const session = useSession();
  const mutation = useMutation();
  const navigate = useNavigate();
  const tid = templateId as api.Template['id'];
  const detail = useApi('admin.templates.get', { params: { templateId: tid } });
  const versions = useApi('admin.templates.versions', { params: { templateId: tid } });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>('editor');
  const canEdit = session.has('template.edit');
  const canPublish = session.has('template.publish');

  const template = detail.data?.template;
  const list = versions.data?.items ?? [];
  const selected = useMemo(() => list.find((v) => v.id === selectedId) ?? detail.data?.latestVersion ?? null, [list, selectedId, detail.data]);

  const [bodyText, setBodyText] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  useEffect(() => {
    if (selected) {
      setBodyText(JSON.stringify(selected.body, null, 2));
      setChangeSummary('');
    }
  }, [selected?.id, selected?.revision]);
  const parsedBody = useMemo<{ value: unknown; error: string | null }>(() => {
    try {
      return { value: JSON.parse(bodyText), error: null };
    } catch (e) {
      return { value: undefined, error: e instanceof Error ? e.message : 'invalid JSON' };
    }
  }, [bodyText]);
  const localValidation = useMemo(() => (template && parsedBody.error === null ? validateTemplateContent(template.type, parsedBody.value) : null), [template, parsedBody]);
  const dirty = selected ? bodyText !== JSON.stringify(selected.body, null, 2) : false;

  const [validation, setValidation] = useState<api.adminTemplates.TemplateValidateResponse | null>(null);
  const [preview, setPreview] = useState<api.adminTemplates.TemplatePreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [fixtureTenant, setFixtureTenant] = useState<string>('halcyon-robotics');
  const [fixtureSize, setFixtureSize] = useState<'S' | 'M' | 'L'>('S');
  const [publishOpen, setPublishOpen] = useState(false);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  useEffect(() => {
    setValidation(null);
    setPreview(null);
    setPreviewError(null);
  }, [selected?.id]);

  const params = selected ? { templateId: tid, versionId: selected.id } : null;
  const inv = ['admin.templates', 'admin.content', 'admin.overview'];

  const save = async () => {
    if (!params || !selected || parsedBody.error) return;
    await mutation.run('admin.templates.update', { params, input: { body: parsedBody.value, changeSummary: changeSummary.trim() || 'Edited body', revision: selected.revision } }, { title: `v${selected.version} saved as draft`, invalidate: inv });
  };
  const newDraft = async () => {
    if (!template || !selected) return;
    const r = await mutation.run('admin.templates.createDraft', { input: { type: template.type, id: template.id, name: template.name, body: parsedBody.error ? selected.body : parsedBody.value, changeSummary: changeSummary.trim() || `Draft from v${selected.version}`, fromVersionId: selected.id } }, { title: 'Draft created', invalidate: inv });
    if (r) setSelectedId(r.version.id);
  };
  const validate = async () => {
    if (!params) return;
    const r = await mutation.run('admin.templates.validate', { params, input: {} }, { title: (d) => (d.valid ? 'Validation passed' : `Validation found ${d.issues.filter((i) => i.severity === 'error').length} errors`), invalidate: inv });
    if (r) {
      setValidation(r);
      setPanel('validation');
    }
  };
  const runPreview = async () => {
    if (!params) return;
    setPreviewError(null);
    const r = await mutation.run('admin.templates.preview', { params, input: { fixtureTenantId: fixtureTenant, fixtureSize, params: {} } }, { title: 'Preview rendered', invalidate: inv });
    if (r) {
      setPreview(r);
      setPanel('preview');
    } else if (mutation.error) setPreviewError(mutation.error);
  };
  const submitReview = async () => {
    if (!params || !selected) return;
    await mutation.run('admin.templates.submitReview', { params, input: { revision: selected.revision } }, { title: 'Submitted for review', invalidate: inv });
  };

  const state = selected?.state;
  const primary = (() => {
    if (!selected || !template) return null;
    if (state === 'draft') return canEdit ? <Button variant="primary" loading={mutation.busy} onClick={() => void validate()}>Validate</Button> : null;
    if (state === 'validated') return <Button variant="primary" loading={mutation.busy} onClick={() => void runPreview()}>Preview against fixtures</Button>;
    if (state === 'previewed') return canEdit ? <Button variant="primary" loading={mutation.busy} onClick={() => void submitReview()}>Submit for review</Button> : null;
    if (state === 'reviewed') return canPublish ? <SensitiveButton variant="primary" onClick={() => setPublishOpen(true)}>Publish</SensitiveButton> : null;
    if (state === 'published' || state === 'rolled_back') return canPublish && template.activeVersionId !== selected.id ? <SensitiveButton variant="primary" onClick={() => setRollbackOpen(true)}>Make active (rollback)</SensitiveButton> : null;
    return null;
  })();

  return (
    <Page>
      <PageHeader
        eyebrow="Templates and rules"
        eyebrowPath={`Templates / ${template?.name ?? '…'}`}
        title={template?.name ?? 'Template'}
        subtitle={template ? `${TEMPLATE_TYPE_SINGULAR[template.type]} · ${template.id} · active ${template.activeVersion ? `v${template.activeVersion}` : '--'} · latest v${template.latestVersion} ${TEMPLATE_STATE_LABEL[template.latestState].toLowerCase()} · ${list.length} versions` : 'Loading.'}
        actions={
          <>
            {canEdit && selected && selected.state !== 'draft' ? <Button variant="tertiary" loading={mutation.busy} onClick={() => void newDraft()}>New draft from v{selected.version}</Button> : null}
            {canPublish && template?.activeVersionId && selected && selected.state !== 'reviewed' && list.some((v) => (v.state === 'published' || v.state === 'rolled_back') && v.id !== template.activeVersionId) ? <SensitiveButton variant="tertiary" onClick={() => setRollbackOpen(true)}>Rollback</SensitiveButton> : null}
            {primary}
          </>
        }
      />
      <ErrorCallout error={detail.error ?? versions.error} onRetry={() => void detail.reload()} className="mt-6" />
      {!template || !selected ? (
        <Skeleton height={320} className="mt-6" />
      ) : (
        <Grid>
          <Card title="Versions" subtitle="Newest first" span="col-span-12 lg:col-span-3" flush>
            <ul className="divide-y divide-hair">
              {list.map((v) => {
                const active = v.id === selected.id;
                return (
                  <li key={v.id}>
                    <button type="button" onClick={() => setSelectedId(v.id)} aria-current={active ? 'true' : undefined} className={`relative flex w-full flex-col gap-1 px-5 py-2.5 text-left transition-colors hover:bg-card ${active ? 'bg-card' : ''}`}>
                      {active ? <span aria-hidden className="absolute inset-y-1 left-0 w-[2px] bg-brand" /> : null}
                      <span className="flex items-center justify-between">
                        <span className="font-mono text-xs-plus text-ink">v{v.version}{template.activeVersionId === v.id ? <span className="ml-2 text-ok">active</span> : null}</span>
                        <Chip tone={TEMPLATE_STATE_TONE[v.state]}>{TEMPLATE_STATE_LABEL[v.state]}</Chip>
                      </span>
                      <span className="truncate text-xs text-mute">{v.changeSummary}</span>
                      <span className="font-mono text-2xs text-dim">{fmtTimestamp(v.updatedAt)} · {v.digest.slice(0, 8)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>
          <div className="col-span-12 lg:col-span-9 flex flex-col gap-6">
            <Card title={`v${selected.version} · ${TEMPLATE_STATE_LABEL[selected.state]}`} subtitle={`schema version ${selected.schemaVersion} · revision ${selected.revision}`} span="" rightSlot={<Chip tone={TEMPLATE_STATE_TONE[selected.state]}>{TEMPLATE_STATE_LABEL[selected.state]}</Chip>}>
              <KeyValue
                layout="grid"
                columns={4}
                items={[
                  { key: 'Author', value: <UserLink userId={selected.authorUserId} /> },
                  { key: 'Reviewer', value: <UserLink userId={selected.reviewerUserId} /> },
                  { key: 'Publisher', value: <UserLink userId={selected.publisherUserId} /> },
                  { key: 'Channel', value: selected.channel ?? '--', mono: true },
                  { key: 'Compatible app', value: selected.compatibleAppVersions, mono: true },
                  { key: 'Validated', value: <Stamp value={selected.validatedAt} /> },
                  { key: 'Previewed', value: <Stamp value={selected.previewedAt} /> },
                  { key: 'Published', value: <Stamp value={selected.publishedAt} /> },
                  { key: 'Release', value: <IdCell value={selected.releaseId} /> },
                  { key: 'Digest', value: <Code>{selected.digest}</Code>, tone: 'ok' },
                ]}
              />
              <p className="mt-3 text-[13px] text-ink/85">{selected.changeSummary}</p>
            </Card>
            <Card title="Content" span="" flush rightSlot={<Tabs compact aria-label="Panels" value={panel} onValueChange={(v) => setPanel(v as Panel)} items={[{ value: 'editor', label: 'Editor' }, { value: 'validation', label: 'Validation', count: (validation?.issues.length ?? localValidation?.issues.length ?? 0) || undefined }, { value: 'preview', label: 'Preview' }, { value: 'diff', label: 'Diff' }]} listClassName="border-0" />}>
              <div className="px-5 py-4">
                {panel === 'editor' ? (
                  <div className="flex flex-col gap-4">
                    {selected.state !== 'draft' ? <Callout tone="info">Published and reviewed versions are immutable. Edits create a new draft version from this body.</Callout> : null}
                    <JsonEditor value={bodyText} onChange={setBodyText} rows={22} label={`Body · ${TEMPLATE_TYPE_SINGULAR[template.type]} · JSON`} />
                    <div className="flex flex-wrap items-end justify-between gap-3">
                      <Field label="Change summary" className="min-w-[320px] flex-1" required>
                        <Input value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} placeholder="What changed and why" />
                      </Field>
                      <div className="flex gap-2">
                        <Button variant="tertiary" disabled={!dirty} onClick={() => setBodyText(JSON.stringify(selected.body, null, 2))}>Discard</Button>
                        {selected.state === 'draft' ? (
                          <Button variant="primary" disabled={!canEdit || !dirty || parsedBody.error !== null || !changeSummary.trim()} loading={mutation.busy} onClick={() => void save()}>Save draft</Button>
                        ) : (
                          <Button variant="primary" disabled={!canEdit || parsedBody.error !== null} loading={mutation.busy} onClick={() => void newDraft()}>Create draft from this</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : panel === 'validation' ? (
                  <ValidationPanel local={localValidation} server={validation} onValidate={canEdit && selected.state === 'draft' ? validate : undefined} busy={mutation.busy} />
                ) : panel === 'preview' ? (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-end gap-3">
                      <Field label="Fixture tenant" className="w-72">
                        <Select value={fixtureTenant} onValueChange={setFixtureTenant} options={CUSTOMERS.filter((c) => c.provider !== 'template_only').map((c) => ({ value: c.slug, label: `${c.displayName} (fixture)`, hint: c.provider.toUpperCase() }))} />
                      </Field>
                      <Field label="Size" className="w-28">
                        <Select value={fixtureSize} onValueChange={(v) => setFixtureSize(v as 'S' | 'M' | 'L')} options={[{ value: 'S', label: 'S' }, { value: 'M', label: 'M' }, { value: 'L', label: 'L (capped to M)' }]} />
                      </Field>
                      <Button variant="primary" loading={mutation.busy} onClick={() => void runPreview()}>Render preview</Button>
                    </div>
                    <ErrorCallout error={previewError} />
                    {!preview ? (
                      <EmptyState compact message="Rendering uses synthetic fixture tenants only; customer data never reaches the server." />
                    ) : (
                      <>
                        <p className="label-mono">Rendered {fmtTimestamp(preview.renderedAt)} · v{preview.version.version} · {preview.warnings.length} warnings · {preview.affectedRules.length} affected rules</p>
                        {preview.warnings.length > 0 ? (
                          <Callout tone="warn" title="Warnings">
                            <ul className="list-disc pl-4">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                          </Callout>
                        ) : null}
                        {preview.affectedRules.length > 0 ? (
                          <div className="flex flex-wrap gap-1">{preview.affectedRules.map((r) => <Chip key={r.canonicalKey} tone={r.change === 'added' ? 'ok' : r.change === 'removed' ? 'brand' : 'warn'}><span className="normal-case">{r.change} {r.canonicalKey}</span></Chip>)}</div>
                        ) : null}
                        {typeof preview.rendered === 'string' ? (
                          TEXT_TYPES.includes(template.type) ? (
                            <div className="rounded-sm border border-line bg-bg px-6 py-5">
                              <div className="prose-agency max-w-none text-[13.5px] leading-[1.7] text-ink/90" dangerouslySetInnerHTML={{ __html: renderMarkdown(preview.rendered) }} />
                            </div>
                          ) : (
                            <pre className="scrollbar-thin max-h-[560px] overflow-auto rounded-sm border border-line bg-bg p-4 font-mono text-xs-plus leading-relaxed text-ink/90">{preview.rendered}</pre>
                          )
                        ) : (
                          <pre className="scrollbar-thin max-h-[560px] overflow-auto rounded-sm border border-line bg-bg p-4 font-mono text-xs-plus leading-relaxed text-ink/90">{JSON.stringify(preview.rendered, null, 2)}</pre>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <DiffPanel templateId={tid} versions={list} initialTo={selected.id} />
                )}
              </div>
            </Card>
          </div>
        </Grid>
      )}
      {template && selected ? <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} templateId={tid} version={selected} onDone={(v) => { setSelectedId(v.id); }} /> : null}
      {template ? <RollbackDialog open={rollbackOpen} onOpenChange={setRollbackOpen} template={template} versions={list} preferred={selected && selected.id !== template.activeVersionId && (selected.state === 'published' || selected.state === 'rolled_back') ? selected.id : null} onDone={(v) => setSelectedId(v.id)} /> : null}
      {mutation.error && mutation.error.code === 'not_found' ? <Button className="mt-4" variant="ghost" onClick={() => void navigate({ to: '/templates' })}>Back to templates</Button> : null}
    </Page>
  );
}

function ValidationPanel({ local, server, onValidate, busy }: { local: ReturnType<typeof validateTemplateContent> | null; server: api.adminTemplates.TemplateValidateResponse | null; onValidate?: () => Promise<void>; busy: boolean }) {
  const issues = server?.issues ?? local?.issues ?? [];
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-mono">{server ? `Server validation · ${server.valid ? 'valid' : 'invalid'}` : local ? `Local check of the current editor body · ${local.ok ? 'no issues' : `${errors.length} errors`}` : 'Body is not valid JSON'}</p>
        {onValidate ? <Button variant="primary" loading={busy} onClick={() => void onValidate()}>Validate on server</Button> : null}
      </div>
      {errors.length === 0 && warnings.length === 0 ? (
        <Callout tone="info">No schema errors or rejected constructs. Executable JavaScript, raw HTML, remote includes and expression evaluation are rejected at validation.</Callout>
      ) : (
        <ul className="divide-y divide-hair rounded-sm border border-line">
          {issues.map((i, idx) => (
            <li key={idx} className="flex items-start gap-3 px-3 py-2 text-[12.5px]">
              <Chip tone={i.severity === 'error' ? 'brand' : 'warn'}>{i.severity}</Chip>
              <span className="font-mono text-xs-plus text-mute">{i.path || '(root)'}</span>
              <span className="flex-1">{i.message}</span>
              <span className="font-mono text-2xs text-dim">{i.code}</span>
            </li>
          ))}
        </ul>
      )}
      {server ? (
        <div>
          <p className="label-mono">Variables</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {server.variables.length === 0 ? <span className="text-xs text-mute">No variables referenced.</span> : server.variables.map((v) => <Chip key={v.name} tone={v.known ? 'ok' : 'brand'}><span className="normal-case">{`{${v.name}}`}</span></Chip>)}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DiffPanel({ templateId, versions, initialTo }: { templateId: api.Template['id']; versions: api.TemplateVersion[]; initialTo: string }) {
  const [to, setTo] = useState(initialTo);
  const toIndex = versions.findIndex((v) => v.id === to);
  // Default to the adjacent older version; when `to` is the oldest, compare against the adjacent newer one.
  const [from, setFrom] = useState<string>(versions[toIndex + 1]?.id ?? versions[toIndex - 1]?.id ?? initialTo);
  const diff = useApi('admin.templates.diff', { params: { templateId }, input: { from: from as api.TemplateVersion['id'], to: to as api.TemplateVersion['id'] } }, { enabled: from !== to && versions.length > 1 });
  const opts = versions.map((v) => ({ value: v.id, label: `v${v.version} · ${TEMPLATE_STATE_LABEL[v.state]}` }));
  if (versions.length < 2) return <EmptyState compact message="A diff needs at least two versions." />;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From" className="w-56"><Select value={from} onValueChange={setFrom} options={opts} /></Field>
        <Field label="To" className="w-56"><Select value={to} onValueChange={setTo} options={opts} /></Field>
      </div>
      <ErrorCallout error={diff.error} onRetry={() => void diff.reload()} />
      {from === to ? (
        <EmptyState compact message="Choose two different versions." />
      ) : diff.loading ? (
        <Skeleton height={200} />
      ) : diff.data ? (
        <>
          <p className="label-mono">{diff.data.hunks.filter((h) => h.kind !== 'equal').length} changed hunks · {diff.data.changedPaths.length} changed paths</p>
          {diff.data.changedPaths.length > 0 ? (
            <ul className="divide-y divide-hair rounded-sm border border-line text-[12.5px]">
              {diff.data.changedPaths.map((c) => (
                <li key={c.path} className="grid grid-cols-[200px_1fr_1fr] gap-3 px-3 py-2">
                  <span className="font-mono text-xs-plus text-brand-text">{c.path}</span>
                  <span className="break-all font-mono text-2xs text-mute">{JSON.stringify(c.before)}</span>
                  <span className="break-all font-mono text-2xs text-ok">{JSON.stringify(c.after)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <pre className="scrollbar-thin max-h-[520px] overflow-auto rounded-sm border border-line bg-bg p-3 font-mono text-xs-plus leading-relaxed">
            {diff.data.hunks.map((h, i) => (
              <span key={i} className={h.kind === 'insert' ? 'block bg-ok/10 text-ok' : h.kind === 'delete' ? 'block bg-brand/10 text-brand-text' : 'block text-mute'}>
                {h.kind === 'insert' ? '+ ' : h.kind === 'delete' ? '- ' : '  '}
                {h.text}
              </span>
            ))}
          </pre>
        </>
      ) : null}
    </div>
  );
}

function PublishDialog({ open, onOpenChange, templateId, version, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; templateId: api.Template['id']; version: api.TemplateVersion; onDone: (v: api.TemplateVersion) => void }) {
  const mutation = useMutation();
  const [channel, setChannel] = useState<api.ReleaseChannel>('stable');
  const [summary, setSummary] = useState('');
  const [compat, setCompat] = useState(version.compatibleAppVersions || '>=1.0.0 <2.0.0');
  const [stage, setStage] = useState<api.RolloutStage>('cohort_3');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Publish v${version.version}`} subtitle="Immutable version · signed release" width={520} actions={<><Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button><SensitiveButton variant="primary" disabled={!summary.trim() || !compat.trim()} loading={mutation.busy} onClick={async () => { const r = await mutation.run('admin.templates.publish', { params: { templateId, versionId: version.id }, input: { channel, changeSummary: summary.trim(), compatibleAppVersions: compat.trim(), rolloutStage: stage, revision: version.revision } }, { title: (d) => `Published v${d.version.version} · release ${d.release.manifest.version}`, invalidate: ['admin.templates', 'admin.content', 'admin.overview'] }); if (r) { onOpenChange(false); onDone(r.version); } }}>Publish</SensitiveButton></>}>
      <div className="flex flex-col gap-4">
        <Field label="Change summary" required><Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Recorded on the release manifest and the publication notice" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Channel"><Select value={channel} onValueChange={(v) => setChannel(v as api.ReleaseChannel)} options={api.RELEASE_CHANNELS.map((c) => ({ value: c, label: c }))} /></Field>
          <Field label="Initial rollout"><Select value={stage} onValueChange={(v) => setStage(v as api.RolloutStage)} options={api.ROLLOUT_STAGES.filter((s) => s !== 'halted').map((s) => ({ value: s, label: ROLLOUT_LABEL[s] }))} /></Field>
        </div>
        <Field label="Compatible app versions" required help="Semver range advertised on the manifest."><Input mono value={compat} onChange={(e) => setCompat(e.target.value)} /></Field>
      </div>
    </Dialog>
  );
}

function RollbackDialog({ open, onOpenChange, template, versions, preferred, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; template: api.Template; versions: api.TemplateVersion[]; preferred: string | null; onDone: (v: api.TemplateVersion) => void }) {
  const mutation = useMutation();
  const candidates = versions.filter((v) => (v.state === 'published' || v.state === 'rolled_back' || v.state === 'active') && v.id !== template.activeVersionId);
  const [toVersionId, setToVersionId] = useState<string>(preferred ?? candidates[0]?.id ?? '');
  const [summary, setSummary] = useState('');
  const defaultTarget = preferred ?? candidates[0]?.id ?? '';
  // The dialog mounts before the versions load; pick the default target each time it opens.
  useEffect(() => {
    if (open) {
      setToVersionId(defaultTarget);
      setSummary('');
    }
  }, [open, defaultTarget]);
  // The server checks the optimistic revision of the version being restored (the target), not the latest one.
  const target = versions.find((v) => v.id === toVersionId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Rollback" subtitle="Republishes an earlier valid version as active" width={500} actions={<><Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button><SensitiveButton variant="primary" disabled={!target || !summary.trim()} loading={mutation.busy} onClick={async () => { if (!target) return; const r = await mutation.run('admin.templates.rollback', { params: { templateId: template.id }, input: { toVersionId: target.id, changeSummary: summary.trim(), revision: target.revision } }, { title: (d) => `Rolled back to v${d.version.version} · release ${d.release.manifest.version}`, invalidate: ['admin.templates', 'admin.content', 'admin.overview'] }); if (r) { onOpenChange(false); onDone(r.version); } }}>Roll back</SensitiveButton></>}>
      <div className="flex flex-col gap-4">
        {candidates.length === 0 ? <Callout tone="warn">No earlier published version exists to roll back to.</Callout> : null}
        <Field label="Target version" required><Select value={toVersionId || undefined} placeholder="Choose a version" onValueChange={setToVersionId} options={candidates.map((v) => ({ value: v.id, label: `v${v.version} · ${TEMPLATE_STATE_LABEL[v.state]}`, hint: v.digest.slice(0, 8) }))} /></Field>
        <Field label="Change summary" required><Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Why this version is being made active again" /></Field>
        <p className="text-xs text-mute">Publication history is retained. Existing operations stay pinned to their version until an operator adopts the change.</p>
      </div>
    </Dialog>
  );
}
